import Swiper from 'swiper';
import { A11y, Navigation, Autoplay, Pagination, EffectFade } from 'swiper/modules';

window.Swiper = Swiper;

const SWIPER_INIT_ATTR = 'data-swiper-init';
const SLIDER_SELECTOR = '.swiper-container';

type SwiperElement = HTMLElement & { swiper?: Swiper };

function isSwiperInitialized(slider: HTMLElement): boolean {
  return slider.getAttribute(SWIPER_INIT_ATTR) === '1' || Boolean((slider as SwiperElement).swiper);
}

function collectSliders(root: ParentNode): HTMLElement[] {
  const list: HTMLElement[] = [];

  if (root instanceof HTMLElement && root.matches(SLIDER_SELECTOR)) {
    list.push(root);
  }

  if (root instanceof Element || root instanceof Document || root instanceof DocumentFragment) {
    root.querySelectorAll<HTMLElement>(SLIDER_SELECTOR).forEach(el => list.push(el));
  }

  return list;
}

function getSwiperOptions(slider: HTMLElement): Record<string, unknown> {
  let uniqueOptions: Record<string, unknown> = {};

  if (slider.classList.contains('product-start__swiper')) {
    uniqueOptions = {
      modules: [Navigation, A11y, Autoplay],
      a11y: true,
      slidesPerView: 1,
      autoHeight: true,
      navigation: {
        nextEl: '.swiper-arrow-next',
        prevEl: '.swiper-arrow-prev'
      },
      breakpoints: {
        320: {
          spaceBetween: 20,
          slidesPerView: 1
        },
        1240: {
          spaceBetween: 20,
          slidesPerView: 2
        }
      }
    };
  }

  if (slider.classList.contains('main-slider__swiper')) {
    const parent = slider.closest('.main-slider');
    const nextBtn = parent?.querySelector<HTMLElement>('.swiper-arrow-next');
    const prevBtn = parent?.querySelector<HTMLElement>('.swiper-arrow-prev');

    uniqueOptions = {
      modules: [Navigation, Autoplay, A11y],
      slidesPerView: 1,
      spaceBetween: 16,
      autoHeight: true,
      loop: false,
      a11y: true,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn
      },
      breakpoints: {
        320: {
          slidesPerView: 2,
          spaceBetween: 8
        },
        700: {
          slidesPerView: 3,
          spaceBetween: 20
        },
        991: {
          slidesPerView: 4,
          spaceBetween: 20
        },
        1240: {
          slidesPerView: 5,
          spaceBetween: 20
        }
      }
    };
  }

  if (slider.classList.contains('swiper-filter')) {
    const parent = slider.closest('.filter__top-line');
    const nextBtn = parent?.querySelector<HTMLElement>('.--prev');
    const prevBtn = parent?.querySelector<HTMLElement>('.--next');

    uniqueOptions = {
      modules: [Navigation, Autoplay, A11y],
      slidesPerView: 'auto',
      spaceBetween: 8,
      slideToClickedSlide: true,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn
      },
      breakpoints: {
        991: {
          spaceBetween: 12
        }
      }
    };
  }

  if (slider.classList.contains('swiper-card-product')) {
    const parent = slider.closest('.card-product__box-swiper');
    const pagi = parent?.querySelector<HTMLElement>('.swiper-card-product-pagination');
    uniqueOptions = {
      modules: [Navigation, Autoplay, A11y, Pagination, EffectFade],
      slidesPerView: 1,
      observer: true,
      observeParents: true,
      speed: 600,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      pagination: {
        el: pagi,
        clickable: true
      },
      breakpoints: {
        1241: {
          allowTouchMove: false
        }
      },
      on: {
        init(this: Swiper): void {
          const paginationEl = this.pagination.el as HTMLElement | null;
          if (!paginationEl) return;

          const bullets = paginationEl.querySelectorAll<HTMLElement>('.swiper-pagination-bullet');
          bullets.forEach((bullet: HTMLElement, index: number) => {
            bullet.addEventListener('mouseenter', () => {
              this.slideTo(index);
            });
          });
        }
      }
    };
  }

  return uniqueOptions;
}

/** Инициализация одного слайдера (без повторного создания инстанса). */
export function initSwiperElement(slider: HTMLElement): void {
  if (!(slider instanceof HTMLElement)) return;
  if (isSwiperInitialized(slider)) return;

  new Swiper(slider, getSwiperOptions(slider) as ConstructorParameters<typeof Swiper>[1]);
  slider.setAttribute(SWIPER_INIT_ATTR, '1');
}

/**
 * Инициализация всех `.swiper-container` внутри root (по умолчанию — document).
 * Безопасно вызывать повторно после AJAX: уже инициализированные пропускаются.
 */
export function initSwiper(root: ParentNode = document): void {
  collectSliders(root).forEach(initSwiperElement);
}

let swiperObserver: MutationObserver | null = null;

function scanAddedNode(node: Node): void {
  if (node instanceof HTMLElement) {
    initSwiper(node);
    return;
  }

  if (node instanceof DocumentFragment) {
    initSwiper(node);
  }
}

/**
 * Следит за DOM и инициализирует новые `.swiper-container` (динамическая подгрузка).
 * Вызывается один раз; повторный вызов игнорируется.
 */
export function observeSwipers(root: ParentNode = document.body): void {
  if (swiperObserver) return;

  const target =
    root instanceof Document ? root.body : root instanceof HTMLElement ? root : null;

  if (!target) return;

  swiperObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(scanAddedNode);
    }
  });

  swiperObserver.observe(target, { childList: true, subtree: true });
}
